import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only editor for the ACH return-code list used by the Bounce Payment dialog.
 * Persisted in `global_settings.bounce_codes`. Resetting reverts to seed defaults.
 */
const BounceCodesEditor = ({ getAuthHeaders }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/transactions/bounce-codes`, { headers: getAuthHeaders() });
      setCodes(res.data || []);
    } catch (err) {
      toast.error('Failed to load bounce codes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const updateRow = (idx, patch) => {
    setCodes(prev => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const moveRow = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= codes.length) return;
    setCodes(prev => {
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeRow = (idx) => {
    if (codes.length <= 1) {
      toast.error('At least one bounce code is required');
      return;
    }
    setCodes(prev => prev.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setCodes(prev => [...prev, { code: '', label: '', default_fee: true, default_fee_amount: 35 }]);
  };

  const handleSave = async () => {
    // Client-side validation
    const cleaned = [];
    const seen = new Set();
    for (const c of codes) {
      const code = (c.code || '').trim().toUpperCase();
      const label = (c.label || '').trim();
      if (!code || !label) {
        toast.error('Every row needs both a code and a label');
        return;
      }
      if (seen.has(code)) {
        toast.error(`Duplicate code: ${code}`);
        return;
      }
      seen.add(code);
      const amt = parseFloat(c.default_fee_amount);
      if (Number.isNaN(amt) || amt < 0) {
        toast.error(`Default fee for ${code} must be a non-negative number`);
        return;
      }
      cleaned.push({ code, label, default_fee: !!c.default_fee, default_fee_amount: amt });
    }
    try {
      setSaving(true);
      await axios.put(`${API}/transactions/bounce-codes`, { codes: cleaned }, { headers: getAuthHeaders() });
      toast.success(`Saved ${cleaned.length} bounce codes`);
      setCodes(cleaned);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save bounce codes');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore default bounce codes? Your custom edits will be lost.')) return;
    try {
      setResetting(true);
      const res = await axios.post(`${API}/transactions/bounce-codes/reset`, {}, { headers: getAuthHeaders() });
      setCodes(res.data?.codes || []);
      toast.success('Restored default bounce codes');
    } catch (err) {
      toast.error('Failed to restore defaults');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card data-testid="bounce-codes-editor-card">
      <CardHeader>
        <CardTitle>Bounce Return Codes</CardTitle>
        <CardDescription>
          Edit the ACH return-code options shown in the Bounce Payment dialog. Changes apply to every admin and every deal immediately after saving.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide pb-1 border-b">
              <div className="col-span-2">Code</div>
              <div className="col-span-5">Label</div>
              <div className="col-span-1 text-center" title="Pre-check the 'charge fee' box in the bounce dialog">Charge?</div>
              <div className="col-span-2 text-center" title="Pre-fill $ amount when this code is selected">Default Fee ($)</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {codes.map((c, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`bounce-code-row-${idx}`}>
                <div className="col-span-2">
                  <Input
                    value={c.code}
                    onChange={(e) => updateRow(idx, { code: e.target.value.toUpperCase() })}
                    placeholder="R01"
                    maxLength={5}
                    data-testid={`bounce-code-input-code-${idx}`}
                  />
                </div>
                <div className="col-span-5">
                  <Input
                    value={c.label}
                    onChange={(e) => updateRow(idx, { label: e.target.value })}
                    placeholder="Insufficient Funds (NSF)"
                    data-testid={`bounce-code-input-label-${idx}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  <Switch
                    checked={!!c.default_fee}
                    onCheckedChange={(v) => updateRow(idx, { default_fee: v })}
                    data-testid={`bounce-code-fee-toggle-${idx}`}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={c.default_fee_amount ?? 35}
                    onChange={(e) => updateRow(idx, { default_fee_amount: e.target.value })}
                    data-testid={`bounce-code-fee-amount-${idx}`}
                    className="text-right"
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => moveRow(idx, -1)} disabled={idx === 0} title="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => moveRow(idx, 1)} disabled={idx === codes.length - 1} title="Move down">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeRow(idx)} title="Remove" className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-3 border-t">
              <Button variant="outline" size="sm" onClick={addRow} data-testid="bounce-code-add-btn">
                <Plus className="h-4 w-4 mr-1" />Add Code
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleReset} disabled={resetting} data-testid="bounce-code-reset-btn">
                  {resetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                  Restore Defaults
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="bounce-code-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BounceCodesEditor;
